import { Box, Button, Checkbox, HStack, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminDeleteUsers, adminGetGuestUsers } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminUser, IAdminUserList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";

export default function Guests() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface ISearchForm {
        keyword: string;
    }

    const { register, watch, handleSubmit, getValues, formState: {errors, isValid}, setError, setValue } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [keyword, setKeyword] = useState<string | null>(null);

    const onSubmit = ({ keyword }: ISearchForm) => {
        setPage(1);
        setKeyword(keyword);
    }

    /* query */

    const { data: userListData, isLoading: isUserListDataLoading } = useQuery<IAdminUserList>({
        queryKey: ['adminGetGuestUsers', page, size, keyword],
        queryFn: () => adminGetGuestUsers({ page, size, keyword }),
        enabled: true,
    });

    const totalPages = useMemo(() => userListData?.totalPages ?? 0, [userListData]);
    const currentPage = useMemo(() => userListData?.currentPage ?? 0, [userListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* user columns */

    const userColumns = [
        { key: "id", label: "ID" },
        { key: "type", label: "타입" },
        { key: "createdAt", label: "생성 일자" },
        { key: "updatedAt", label: "수정 일자" }
    ]

    /* selection */

    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const currentPageUserIds = useMemo(() => userListData?.users.map((user) => user.id) ?? [], [userListData]);

    const isAllCurrentPageSelected = useMemo(() => {
        return currentPageUserIds.length > 0 && currentPageUserIds.every((id) => selectedUserIds.includes(id));
    }, [currentPageUserIds, selectedUserIds]);

    const isSomeCurrentPageSelected = useMemo(() => {
        const someSelected = currentPageUserIds.some((id) => selectedUserIds.includes(id));
        return someSelected && !isAllCurrentPageSelected;
    }, [currentPageUserIds, selectedUserIds, isAllCurrentPageSelected]);

    const toggleSelectAllCurrentPage = () => {
        if (isAllCurrentPageSelected) {
            setSelectedUserIds((prev) => prev.filter((id) => !currentPageUserIds.includes(id)));
        } else {
            setSelectedUserIds((prev) => Array.from(new Set([...prev, ...currentPageUserIds])));
        }
    };

    const toggleSelectOne = (userId: string) => {
        setSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
    };

    /* delete users modal */

    const { isOpen:isDeleteUsersModalOpen, onOpen:onOpenDeleteUsersModal, onClose:onCloseDeleteUsersModal } = useDisclosure();

    const onDeleteUsers = () => {
        deleteUsersMutation.mutate({ userIds: selectedUserIds });
        onCloseDeleteUsersModal();
    }

    /* queryClient */

    const queryClient = useQueryClient();

    /* toast */

    const toast = useToast();

    /* mutation */

    const deleteUsersMutation = useMutation({
        mutationFn: adminDeleteUsers,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetGuestUsers', page, size] });
            setSelectedUserIds([]);
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "게스트 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* render */

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>

                {/* header */}

                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>게스트</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{userListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                    </HStack>
                </HStack>

                {/* search form */}

                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("keyword", {required:false})}
                        type="text" 
                        placeholder="Search" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* trash button */}

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button 
                        size={"sm"} 
                        variant={"solid"} 
                        isDisabled={selectedUserIds.length === 0} 
                        opacity={selectedUserIds.length === 0 ? 0.1 : 1}
                        onClick={onOpenDeleteUsersModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Modal isOpen={isDeleteUsersModalOpen} onClose={onCloseDeleteUsersModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {selectedUserIds.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteUsers}
                                    isLoading={deleteUsersMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {/* table */}

                {!isUserListDataLoading ? (
                    <TableContainer marginTop={5}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    <Th>
                                        <Checkbox 
                                            isChecked={isAllCurrentPageSelected} 
                                            isIndeterminate={isSomeCurrentPageSelected} 
                                            onChange={toggleSelectAllCurrentPage}
                                        />
                                    </Th>
                                    {userColumns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {userListData?.users.map((user) => {
                                    return (
                                    <Tr key={user.id}>
                                        <Td>
                                            <Checkbox 
                                                isChecked={selectedUserIds.includes(user.id)}
                                                onChange={() => toggleSelectOne(user.id)}
                                            />
                                        </Td>
                                        {userColumns.map((column, index) => (
                                            <Td key={index}>
                                                {(column.key === "createdAt" || column.key === "updatedAt")
                                                    ? formatDateTimeKor(user[column.key as keyof IAdminUser] as string)
                                                    : String(user[column.key as keyof IAdminUser] ?? "-")}
                                            </Td>
                                        ))}
                                    </Tr>
                                )})}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}